import * as _ from 'lodash-es';
import {Observable, Observer, Subscription} from 'rxjs';
import {finalize, map, pairwise, startWith, tap} from 'rxjs/operators';

/**
 * RxJs pipeable operator.
 * Merges array of observables to the output observable.
 * Each new array from the source updates the output by calculating the difference with the previous array.
 * Each item from the source's array is converted to an observable using the passed factory.
 * The resulting observables then are merged to the output.
 * The similar operators are:
 *  - 'merge' - merges a list of known observables, doesn't allow to dynamically change the list;
 *  - 'mergeAll' - adds each received observable to the output, doesn't allow to remove any;
 *  - 'switchMap' in a combination with 'merge' - recreates the output on each update,
 *  causing resubscribing to already known items.
 * @param {(item: T) => Observable<R>} itemObservableFactory
 * @returns {(source: Observable<T>) => Observable<T>}
 */
export const incrementalMerge = <T, R>(itemObservableFactory: (item: T) => Observable<R>) =>
    (source: Observable<T[]>): Observable<R> =>
        new Observable((observer: Observer<R>) => {
            const subscriptionsMap = new Map<T, Subscription>();
            const subscribe = (item: T) => subscriptionsMap.set(
                item,
                itemObservableFactory(item)
                    .subscribe(value => observer.next(value))
            );
            const unsubscribe = (item: T) => {
                subscriptionsMap.get(item).unsubscribe();
                subscriptionsMap.delete(item);
            };

            return source.pipe(
                map(list => list && list.slice()), // Break the reference allowing to pass the same array object.
                startWith([]),
                pairwise(),
                tap(([prevList, list]) => {
                    const removed: T[] = _.difference(prevList, list);
                    const added: T[]   = _.difference(list, prevList);

                    removed.forEach(unsubscribe);
                    added.forEach(subscribe);
                }),
                finalize(() =>
                    subscriptionsMap.forEach((subscription, item) => unsubscribe(item))
                )
            ).subscribe();
        });
